import { useEffect, useMemo, useRef } from 'react';
import { usePage } from '@inertiajs/react';
import Intercom, { boot, show, shutdown, update } from '@intercom/messenger-js-sdk';
import type { InitType as IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';

import { getDocument } from '@/lib/utils';
import type { Account, CurrentUser } from '@/types/shared';

type IntercomUser = Pick<CurrentUser, 'name' | 'email' | 'id'> | null;
type IntercomWindow = Window & {
  Intercom?: (...args: unknown[]) => unknown;
  intercomSettings?: IntercomSettings;
};
type IntercomPageProps = {
  currentUser?: CurrentUser | null;
  userAccounts?: Account[];
  accountId?: string | number;
  intercom?: {
    appId?: string | null;
    baseUrl?: string | null;
  } | null;
};

function intercomUserDetails(user: IntercomUser) {
  if (!user) return {};

  return {
    name: user.name ?? undefined,
    email: user.email ?? undefined,
    // biome-ignore lint/style/useNamingConvention: Intercom API requires snake_case keys
    user_id: user.id ?? undefined,
  };
}

function intercomCompanyDetails(
  userAccounts: Account[] | undefined,
  accountId: string | number | undefined,
) {
  if (!userAccounts?.length || !accountId) {
    return {};
  }

  const activeAccount = userAccounts.find((account) => String(account.id) === String(accountId));
  if (!activeAccount) {
    return {};
  }

  return {
    company: {
      // biome-ignore lint/style/useNamingConvention: Intercom API requires snake_case keys
      company_id: activeAccount.id,
      name: activeAccount.name,
    },
  };
}

function buildIntercomSettings(
  appId: string,
  baseUrl: string,
  currentUser?: IntercomUser,
  userAccounts?: Account[],
  accountId?: string | number,
): IntercomSettings {
  return {
    // biome-ignore lint/style/useNamingConvention: Intercom API requires snake_case keys
    api_base: baseUrl,
    // biome-ignore lint/style/useNamingConvention: Intercom API requires snake_case keys
    app_id: appId,
    // biome-ignore lint/style/useNamingConvention: Intercom API requires snake_case keys
    hide_default_launcher: true,
    ...intercomUserDetails(currentUser ?? null),
    ...intercomCompanyDetails(userAccounts, accountId),
  };
}

export function showIntercomMessenger(): boolean {
  const document = getDocument();
  const window = document?.defaultView as IntercomWindow | null;

  if (typeof window?.Intercom !== 'function') {
    console.warn('Intercom is not available');
    return false;
  }

  show();
  return true;
}

export function resetIntercomSession(): boolean {
  const document = getDocument();
  const window = document?.defaultView as IntercomWindow | null;

  if (typeof window?.Intercom !== 'function') {
    delete window?.intercomSettings;
    return false;
  }

  shutdown();
  delete window.intercomSettings;
  return true;
}

function IntercomWidget() {
  const page = usePage<IntercomPageProps>();
  const pageUrl = page.url;
  const { intercom, currentUser, userAccounts, accountId } = page.props;
  const intercomAppId = intercom?.appId ?? null;
  const intercomBaseUrl = intercom?.baseUrl;
  const hasBootedRef = useRef(false);
  const settings = useMemo(
    () =>
      intercomAppId && intercomBaseUrl
        ? buildIntercomSettings(
            intercomAppId,
            intercomBaseUrl,
            currentUser ?? null,
            userAccounts,
            accountId,
          )
        : null,
    [accountId, currentUser, intercomAppId, intercomBaseUrl, userAccounts],
  );

  useEffect(() => {
    if (!settings || !pageUrl) return;

    Intercom(settings);

    if (!hasBootedRef.current) {
      boot(settings);
      hasBootedRef.current = true;
      return;
    }

    update(settings);
  }, [pageUrl, settings]);

  useEffect(() => {
    return () => {
      const document = getDocument();
      const window = document?.defaultView as IntercomWindow | null;

      if (hasBootedRef.current) {
        shutdown();
        hasBootedRef.current = false;
      }

      delete window?.intercomSettings;
    };
  }, []);

  return null;
}

export default IntercomWidget;
